/**
 * Shared formatting helpers. Keep these dumb and pure — no date libs,
 * no locale explosion, no network. Anything that needs a contract should
 * accept a plain value, not the whole object.
 */

// DISPLAY-side aliases for free-form currency spellings (doc-190). Strings like
// 'US Dollars' really do sit in contracts.currency, and for those rows a '$' is
// not a fallback, it is the right answer.
//
// This table is a deliberate COPY of the one in backend/serializers/contracts.js
// (doc-207). That file is the AUTHORITY — normalizeCurrency there canonicalises
// every write, so anything stored from doc-207 onward is already a code. This
// copy is the DISPLAY FALLBACK for rows written before that shipped, which were
// never backfilled. There is no shared package in this repo, so the two tables
// are kept in step by hand: change one, change the other.
//
// FENCED ON AMBIGUITY: only spellings that name their currency unambiguously
// are mapped. A bare '$' is USD/CAD/AUD/MXN/SGD…, bare 'DOLLAR(S)' likewise,
// bare 'POUND(S)' is GBP or EGP, '¥' is JPY or CNY, and 'USDT' is a token, not
// a currency. Guessing a symbol for any of those would re-introduce exactly the
// wrong-symbol bug doc-187 removed, so they fall through to the bare-number
// branch below. This map does NOT clean the stored data — it only formats it.
const CURRENCY_ALIASES = {
  'US DOLLARS': 'USD',
  'US DOLLAR': 'USD',
  'U.S. DOLLARS': 'USD',
  'US$': 'USD',
  'USD$': 'USD',
  EURO: 'EUR',
  EUROS: 'EUR',
  'POUNDS STERLING': 'GBP',
  STERLING: 'GBP',
  'GBP£': 'GBP',
  '£': 'GBP',
};

/**
 * Free-form currency string → a 3-letter code Intl can format, or null when it
 * cannot be resolved.
 *
 * The accept test is a PURE REGEX on purpose: Intl's accept set for the options
 * below is exactly /^[A-Za-z]{3}$/ (measured — unassigned codes like 'ZZZ'
 * format fine as a bare code, and 'XXX' is the real ISO "no currency" code that
 * renders '¤'). So no ISO-4217 table and no try/catch probe is needed; three
 * ASCII letters is necessary and sufficient.
 */
export const resolveCurrencyCode = (raw) => {
  const cleaned = String(raw ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
  if (!cleaned) return null;
  if (CURRENCY_ALIASES[cleaned]) return CURRENCY_ALIASES[cleaned];
  return /^[A-Z]{3}$/.test(cleaned) ? cleaned : null;
};

// A missing/blank currency means "the default", not "unresolvable".
const displayCode = (currency) => (
  currency == null || String(currency).trim() === '' ? 'USD' : resolveCurrencyCode(currency)
);

// Longest raw currency string printed inline before it is clamped (doc-221).
// Deliberately above the 15 chars of 'US DOLLARS ONLY' — the longest value any
// existing test or observed row carries — so the clamp only ever fires on the
// pathological shapes, never on the ones already shipping.
export const CURRENCY_NOTE_MAX = 24;

/**
 * The "currency on file" clause, shared by the Overview fact strip and the
 * Counterparty 360 Active-value tile (doc-221). Both surfaces printed the raw
 * stored string with no clamp and no trim, which gave two defects:
 *
 *  1. A long free-form value ('UNITED STATES DOLLARS PAYABLE PER SECTION
 *     4.2(b)…', 67 chars — nothing on the write path length-bounds it) wrapped
 *     the sub-line to three lines and grew the whole equal-height fact strip.
 *  2. A padded legacy value ('   $   ') printed its padding INSIDE the quotes.
 *     Only pre-doc-207 rows can carry this: normalizeCurrency in
 *     backend/serializers/contracts.js trims every write since.
 *
 * So: trim for DISPLAY (the emptiness test already trimmed — that split was the
 * whole of defect 2), then clamp, preferring a word boundary so the tail reads
 * as words rather than a cut token. Nothing is lost from either page — the
 * caller puts `title` on the element, and the contract's Key Clauses ›
 * Commercial › Currency field still shows the value untruncated.
 *
 * @returns {{text: string, title: string|null}|null} null when the code
 *   resolves or the value is blank (a blank currency is the USD default, NOT an
 *   unresolved one); `title` is null unless the text was actually clamped.
 */
export const currencyOnFileNote = (raw) => {
  const cleaned = String(raw ?? '').trim().replace(/\s+/g, ' ');
  if (!cleaned || resolveCurrencyCode(cleaned)) return null;
  const phrase = (value) => `currency on file "${value}"`;
  if (cleaned.length <= CURRENCY_NOTE_MAX) return { text: phrase(cleaned), title: null };
  const hard = cleaned.slice(0, CURRENCY_NOTE_MAX - 1);
  const lastSpace = hard.lastIndexOf(' ');
  // Break on a word only when it keeps at least half the budget — otherwise an
  // early space would clamp a 67-char string down to a couple of letters.
  const kept = lastSpace >= CURRENCY_NOTE_MAX / 2 ? hard.slice(0, lastSpace) : hard.trimEnd();
  return { text: phrase(`${kept}…`), title: phrase(cleaned) };
};

// minimumFractionDigits MUST stay explicit on every branch. Leaving it out is
// not the same as 0 for style 'currency': its default is the currency's own
// digit count (2 for USD), and older V8 resolves it to min(2, max) = 1 — so
// Node 20 (CI) rendered '$999.0'/'$1.0K' while Node 24 (dev) rendered
// '$999'/'$1K'. Pinning it to 0 makes the output identical on both.
const compactPlain = (num) => new Intl.NumberFormat('en-US', {
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
}).format(num);

/**
 * Compact money for table cells and tiles — "$1.5K", "€500K", "¥12M".
 * Honors the currency code (it used to take one and hardcode a "$", so every
 * non-USD value rendered as dollars — doc-187). Codes en-US has no symbol for
 * render as the bare code, e.g. "SGD 500K", which is correct Intl output.
 *
 * An unresolvable code renders the bare compact NUMBER — no symbol, no code
 * inline (doc-190). This helper's output lands in ~110px right-aligned table
 * cells, where the old '500000 US DOLLARS' fallback wrapped to three lines and
 * inflated the row; and printing a '$' instead would be the doc-187 bug again.
 * Beside siblings that do show '$1.2M', a bare number reads as "currency
 * unknown" by contrast, which is honest.
 */
export const fmtMoney = (n, currency = 'USD') => {
  if (n == null || n === '' || isNaN(Number(n))) return '—';
  const num = Number(n);
  const code = displayCode(currency);
  if (!code) return compactPlain(num);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      notation: 'compact',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(num);
  } catch (_) {
    // Unreachable now that resolveCurrencyCode gates the code (Intl throws only
    // on a code outside /^[A-Za-z]{3}$/). Kept as belt and braces: an escaping
    // RangeError would blank the whole row.
    return compactPlain(num);
  }
};

const plainNumber = (value) => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(value));

/** Full money form — "$500,000", "€500,000", or a bare "500,000" (see fmtMoney). */
export const fmtCurrency = (value, currency = 'USD', { empty = null } = {}) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return empty;
  const code = displayCode(currency);
  if (!code) return plainNumber(value);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(Number(value));
  } catch (_) {
    return plainNumber(value); // unreachable — see fmtMoney's catch
  }
};

export const fmtCurrencyOrNull = (value, currency = 'USD') => fmtCurrency(value, currency, { empty: null });

/**
 * Parse a date value for calendar-day display. Date-only strings
 * ('YYYY-MM-DD', how contract dates are stored) must parse as LOCAL
 * midnight — new Date() treats them as UTC midnight, which renders a
 * day early in timezones west of UTC.
 */
const parseDateLocal = (d) => {
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day);
  }
  return new Date(d);
};

export const fmtDate = (d, empty = '—') => {
  if (!d) return empty;
  const date = parseDateLocal(d);
  if (isNaN(date)) return empty;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const fmtDateOrNull = (d) => fmtDate(d, null);

export const fmtDateShort = (d, empty = '—') => {
  if (!d) return empty;
  const date = parseDateLocal(d);
  if (isNaN(date)) return empty;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const fmtDateTime = (d, empty = '—') => {
  if (!d) return empty;
  const date = new Date(d);
  if (isNaN(date)) return empty;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const fmtDateTimeOrEmpty = (d) => fmtDateTime(d, '');

export const fmtShortDateTime = (d, empty = '—') => {
  if (!d) return empty;
  const date = new Date(d);
  if (isNaN(date)) return empty;
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const fmtShortDateTimeOrEmpty = (d) => fmtShortDateTime(d, '');

/** Positive = future, negative = past. */
export const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const A = parseDateLocal(a); const B = parseDateLocal(b);
  return Math.round((B - A) / 86400000);
};

export const relativeTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date)) return '—';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return fmtDateShort(date);
};

export const formatYesNo = (value, empty = null) => {
  if (value === true || value === 'yes') return 'Yes';
  if (value === false || value === 'no') return 'No';
  return empty;
};

export const humanizeEnum = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatFieldLabel = (field) => String(field || '')
  .replace(/_/g, ' ')
  .replace(/\bis (\w)/i, (_, char) => char.toUpperCase())
  .replace(/^\w/, (char) => char.toUpperCase());

export const formatAnswerValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value);
  if (/^[a-z][a-z0-9_]*$/.test(text) && text.includes('_')) {
    return text.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
  }
  return text;
};

export const formatList = (value, empty = 'none') => {
  if (!Array.isArray(value) || value.length === 0) return empty;
  return value.join(', ');
};
